import AddIcon from "@mui/icons-material/Add";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LoopIcon from "@mui/icons-material/Loop";
import RestoreIcon from "@mui/icons-material/Restore";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { Alert, Box, Button, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import { getColumns } from "./getColumns";
import BedrockIdeaService from "./BedrockIdeaService";
import Logger from "../../../services/Logger";
import BiContSchedulerDialog from "./BiContSchedulerDialog";

const SEARCH_STORAGE_KEY = "admin.bedrock-idea.search";
const FILTER_GEN_HIDDEN_WORDSEARCH_KEY = "admin.bedrock-idea.filter.genHiddenWordsearch";
const FILTER_GEN_WORD_WHEEL_KEY = "admin.bedrock-idea.filter.genWordWheel";
const FILTER_GEN_STANDALONE_IMAGE_KEY = "admin.bedrock-idea.filter.genStandaloneImage";
const FILTER_GEN_WORD_SEARCH_9_KEY = "admin.bedrock-idea.filter.genWordSearch9";
const FILTER_EXHAUSTED_KEY = "admin.bedrock-idea.filter.exhausted";
const FILTER_TAG_KEY = "admin.bedrock-idea.filter.tag";
const SORT_STORAGE_KEY = "admin.bedrock-idea.sort";
const GRID_COLUMNS_KEY = "admin.bedrock-idea.grid.columns";
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];
const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_SORT_MODEL = [{ field: "latestGenDate", sort: "desc" }];
const SORT_OPTIONS = [
    { value: "createdAt:desc", label: "Created at (desc)" },
    { value: "createdAt:asc", label: "Created at (asc)" },
    { value: "latestGenDate:desc", label: "Latest gen (desc)" },
    { value: "latestGenDate:asc", label: "Latest gen (asc)" },
];
const DEFAULT_SEARCH_PREFS = {
    tag: null,
    exhausted: "all",
    continuous: "all",
    genHiddenWordsearch: false,
    genWordWheel: false,
    genStandaloneImage: false,
    genWordSearch9: false,
    sort: DEFAULT_SORT_MODEL,
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
};

function applyColumnOrder(columns, order) {
    if (!Array.isArray(order) || order.length === 0) {
        return columns;
    }
    const byField = Object.fromEntries(columns.map((col) => [col.field, col]));
    const ordered = [];
    const seen = new Set();
    for (const field of order) {
        if (byField[field]) {
            ordered.push(byField[field]);
            seen.add(field);
        }
    }
    for (const col of columns) {
        if (!seen.has(col.field)) {
            ordered.push(col);
        }
    }
    return ordered;
}

function loadGridColumnState() {
    try {
        const raw = localStorage.getItem(GRID_COLUMNS_KEY);
        if (!raw) {
            return { columnVisibilityModel: {}, columnWidths: {}, columnOrder: [], pageSize: DEFAULT_PAGE_SIZE };
        }
        const parsed = JSON.parse(raw);
        const pageSize = Number(parsed?.pageSize);
        const columnOrder = Array.isArray(parsed?.columnOrder)
            ? parsed.columnOrder.filter((field) => typeof field === "string")
            : [];
        return {
            columnVisibilityModel:
                parsed?.columnVisibilityModel && typeof parsed.columnVisibilityModel === "object"
                    ? parsed.columnVisibilityModel
                    : {},
            columnWidths:
                parsed?.columnWidths && typeof parsed.columnWidths === "object"
                    ? parsed.columnWidths
                    : {},
            columnOrder,
            pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
        };
    } catch (error) {
        Logger.error("Error reading bedrock idea grid columns from localStorage:", error);
        return { columnVisibilityModel: {}, columnWidths: {}, columnOrder: [], pageSize: DEFAULT_PAGE_SIZE };
    }
}

function saveGridColumnState(state) {
    try {
        localStorage.setItem(GRID_COLUMNS_KEY, JSON.stringify(state));
    } catch (error) {
        Logger.error("Error saving bedrock idea grid columns to localStorage:", error);
    }
}

function sortOptionValue(model) {
    const field = model?.[0]?.field;
    const direction = model?.[0]?.sort;
    const value = `${field}:${direction}`;
    return SORT_OPTIONS.some((option) => option.value === value) ? value : "";
}

function normalizeSortModel(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_SORT_MODEL;
    const first = raw[0];
    const field = typeof first?.field === "string" ? first.field.trim() : "";
    const sort = first?.sort === "asc" || first?.sort === "desc" ? first.sort : "";
    if (!field || !sort) return DEFAULT_SORT_MODEL;
    return [{ field, sort }];
}

function readLegacySearchPrefs() {
    let tag = null;
    try {
        const stored = localStorage.getItem(FILTER_TAG_KEY);
        if (typeof stored === "string" && stored.trim()) tag = stored.trim();
    } catch {}
    let exhausted = "all";
    try {
        const stored = localStorage.getItem(FILTER_EXHAUSTED_KEY);
        if (stored === "yes" || stored === "no") exhausted = stored;
    } catch {}
    const readBool = (key) => {
        try {
            return localStorage.getItem(key) === "true";
        } catch {
            return false;
        }
    };
    let sort = DEFAULT_SORT_MODEL;
    try {
        const raw = localStorage.getItem(SORT_STORAGE_KEY);
        if (raw) sort = normalizeSortModel(JSON.parse(raw));
    } catch {}
    const { pageSize } = loadGridColumnState();
    return {
        ...DEFAULT_SEARCH_PREFS,
        tag,
        exhausted,
        genHiddenWordsearch: readBool(FILTER_GEN_HIDDEN_WORDSEARCH_KEY),
        genWordWheel: readBool(FILTER_GEN_WORD_WHEEL_KEY),
        genStandaloneImage: readBool(FILTER_GEN_STANDALONE_IMAGE_KEY),
        genWordSearch9: readBool(FILTER_GEN_WORD_SEARCH_9_KEY),
        sort,
        pageSize,
    };
}

function loadSearchPrefs() {
    try {
        const raw = localStorage.getItem(SEARCH_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const page = Number(parsed?.page);
            const pageSize = Number(parsed?.pageSize);
            const tag = typeof parsed?.tag === "string" && parsed.tag.trim() ? parsed.tag.trim() : null;
            const exhausted =
                parsed?.exhausted === "yes" || parsed?.exhausted === "no" ? parsed.exhausted : "all";
            const continuous = parsed?.continuous === "only" ? "only" : "all";
            return {
                tag,
                exhausted,
                continuous,
                genHiddenWordsearch: parsed?.genHiddenWordsearch === true,
                genWordWheel: parsed?.genWordWheel === true,
                genStandaloneImage: parsed?.genStandaloneImage === true,
                genWordSearch9: parsed?.genWordSearch9 === true,
                sort: normalizeSortModel(parsed?.sort),
                page: Number.isInteger(page) && page >= 0 ? page : 0,
                pageSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
            };
        }
    } catch (error) {
        Logger.error("Error reading bedrock idea search prefs from localStorage:", error);
    }
    return readLegacySearchPrefs();
}

function saveSearchPrefs(prefs) {
    try {
        localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(prefs));
        if (prefs.tag) localStorage.setItem(FILTER_TAG_KEY, prefs.tag);
        else localStorage.removeItem(FILTER_TAG_KEY);
        localStorage.setItem(FILTER_EXHAUSTED_KEY, prefs.exhausted || "all");
        localStorage.setItem(FILTER_GEN_HIDDEN_WORDSEARCH_KEY, prefs.genHiddenWordsearch ? "true" : "false");
        localStorage.setItem(FILTER_GEN_WORD_WHEEL_KEY, prefs.genWordWheel ? "true" : "false");
        localStorage.setItem(FILTER_GEN_STANDALONE_IMAGE_KEY, prefs.genStandaloneImage ? "true" : "false");
        localStorage.setItem(FILTER_GEN_WORD_SEARCH_9_KEY, prefs.genWordSearch9 ? "true" : "false");
        localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(prefs.sort));
    } catch (error) {
        Logger.error("Error saving bedrock idea search prefs to localStorage:", error);
    }
}

export default function BedrockIdeaMain() {
    const navigate = useNavigate();
    const location = useLocation();
    const [ideas, setIdeas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState();

    // Pagination state
    const [paginationModel, setPaginationModel] = useState(() => {
        const prefs = loadSearchPrefs();
        return { page: prefs.page, pageSize: prefs.pageSize };
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        () => loadGridColumnState().columnVisibilityModel
    );
    const [columnWidths, setColumnWidths] = useState(() => loadGridColumnState().columnWidths);
    const [columnOrder, setColumnOrder] = useState(() => loadGridColumnState().columnOrder);
    const [totalRows, setTotalRows] = useState(0);
    const [sortModel, setSortModel] = useState(() => loadSearchPrefs().sort);

    // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    // Execute confirmation dialog state
    const [openExecuteDialog, setOpenExecuteDialog] = useState(false);
    const [ideaToExecute, setIdeaToExecute] = useState(null);

    const [openRoundRobinDialog, setOpenRoundRobinDialog] = useState(false);
    const [roundRobinSchedulerOn, setRoundRobinSchedulerOn] = useState(null);

    const [openResetContinuousDialog, setOpenResetContinuousDialog] = useState(false);
    const [resettingContinuous, setResettingContinuous] = useState(false);
    const [restoringStandalone, setRestoringStandalone] = useState(false);

    const [availableTags, setAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(true);
    const [selectedTag, setSelectedTag] = useState(() => loadSearchPrefs().tag);
    const [filterGenHiddenWordsearch, setFilterGenHiddenWordsearch] = useState(
        () => loadSearchPrefs().genHiddenWordsearch
    );
    const [filterGenWordWheel, setFilterGenWordWheel] = useState(() => loadSearchPrefs().genWordWheel);
    const [filterGenStandaloneImage, setFilterGenStandaloneImage] = useState(
        () => loadSearchPrefs().genStandaloneImage
    );
    const [filterGenWordSearch9, setFilterGenWordSearch9] = useState(
        () => loadSearchPrefs().genWordSearch9
    );
    const [filterExhausted, setFilterExhausted] = useState(() => loadSearchPrefs().exhausted);
    const [filterContinuous, setFilterContinuous] = useState(() => loadSearchPrefs().continuous);

    useEffect(() => {
        saveSearchPrefs({
            tag: selectedTag,
            exhausted: filterExhausted,
            continuous: filterContinuous,
            genHiddenWordsearch: filterGenHiddenWordsearch,
            genWordWheel: filterGenWordWheel,
            genStandaloneImage: filterGenStandaloneImage,
            genWordSearch9: filterGenWordSearch9,
            sort: sortModel,
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
        });
    }, [
        selectedTag,
        filterExhausted,
        filterContinuous,
        filterGenHiddenWordsearch,
        filterGenWordWheel,
        filterGenStandaloneImage,
        filterGenWordSearch9,
        sortModel,
        paginationModel.page,
        paginationModel.pageSize,
    ]);

    useEffect(() => {
        saveGridColumnState({
            columnVisibilityModel,
            columnWidths,
            columnOrder,
            pageSize: paginationModel.pageSize,
        });
    }, [columnVisibilityModel, columnWidths, columnOrder, paginationModel.pageSize]);

    useEffect(() => {
        const loadTags = async () => {
            setLoadingTags(true);
            const result = await BedrockIdeaService.listTags();
            if (result.success && result.data && Array.isArray(result.data)) {
                setAvailableTags(result.data);
            }
            setLoadingTags(false);
        };
        loadTags();
    }, []);

    useEffect(() => {
        fetchIdeas();
    }, [reload, paginationModel, sortModel, selectedTag, filterGenHiddenWordsearch, filterGenWordWheel, filterGenStandaloneImage, filterGenWordSearch9, filterExhausted, filterContinuous]);

    const fetchRoundRobinSchedulerStatus = async () => {
        const result = await BedrockIdeaService.getRoundRobinSchedulerConfig();
        if (result.success && result.data) {
            const { enabled, countDown } = result.data;
            const count = countDown != null ? Number(countDown) : 0;
            setRoundRobinSchedulerOn(Boolean(enabled) && !isNaN(count) && count > 0);
        }
    };

    useEffect(() => {
        fetchRoundRobinSchedulerStatus();
    }, []);

    const fetchIdeas = async () => {
        setLoading(true);
        const sort =
            sortModel.length > 0 && sortModel[0].field && sortModel[0].sort
                ? `${sortModel[0].field}:${sortModel[0].sort}`
                : "createdAt:desc";
        const exhaustedFilter =
            filterExhausted === "yes" ? true : filterExhausted === "no" ? false : undefined;
        const result = await BedrockIdeaService.listIdeas(
            paginationModel.page,
            paginationModel.pageSize,
            sort,
            selectedTag ? [selectedTag] : null,
            {
                genHiddenWordsearch: filterGenHiddenWordsearch,
                genWordWheel: filterGenWordWheel,
                genStandaloneImage: filterGenStandaloneImage,
                genWordSearch9: filterGenWordSearch9,
                exhausted: exhaustedFilter,
                roundRobin: filterContinuous === "only" ? true : undefined,
            }
        );
        if (result.success && result.data) {
            const content = result.data.content || [];
            const total = result.data.page?.totalElements ?? 0;
            setIdeas(content);
            setTotalRows(total);
            const lastPage = Math.max(0, Math.ceil(total / paginationModel.pageSize) - 1);
            if (paginationModel.page > lastPage) {
                setPaginationModel((prev) => ({ ...prev, page: lastPage }));
            }
        } else {
            handleSnackBar("error", result.error || "Failed to fetch ideas");
        }
        setLoading(false);
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleOpenCreate = () => {
        navigate("/admin/bedrock-idea/new");
    };

    const handleOpenEdit = (idea) => {
        navigate(`/admin/bedrock-idea/edit/${idea.id}`);
    };

    useEffect(() => {
        const msg = location.state?.message;
        if (msg) {
            setSeverity("success");
            setMessage(msg);
            setOpenSnackbar(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.message, location.pathname, navigate]);

    const handleExecuteClick = (idea) => {
        setIdeaToExecute(idea);
        setOpenExecuteDialog(true);
    };

    const handleConfirmExecute = async (id) => {
        setOpenExecuteDialog(false);
        handleSnackBar("info", `Executing idea: ${ideaToExecute?.title}...`);
        const result = await BedrockIdeaService.executeIdea(id);
        if (result.success) {
            handleSnackBar("success", `Idea "${ideaToExecute?.title}" executed successfully`);
        } else {
            handleSnackBar("error", result.error);
        }
        setIdeaToExecute(null);
    };

    const handleToggleRoundRobin = async (idea, newRoundRobinValue) => {
        if (idea?.exhausted && newRoundRobinValue) {
            handleSnackBar("warning", "Continuous stays off while this idea is exhausted");
            return;
        }
        try {
            const result = await BedrockIdeaService.updateIdea(idea.id, { roundRobin: newRoundRobinValue });
            if (result.success) {
                setIdeas(prevIdeas =>
                    prevIdeas.map(item =>
                        item.id === idea.id ? { ...item, roundRobin: newRoundRobinValue } : item
                    )
                );
                handleSnackBar("success", `Continuous ${newRoundRobinValue ? "enabled" : "disabled"} for "${idea.title}"`);
            } else {
                handleSnackBar("error", result.error || "Failed to update Continuous");
                setReload(moment());
            }
        } catch (error) {
            Logger.error("Error toggling Continuous:", error);
            handleSnackBar("error", "Failed to update Continuous. Please try again.");
            setReload(moment());
        }
    };

    const handleOpenRoundRobin = () => setOpenRoundRobinDialog(true);

    const handleConfirmResetAllContinuous = async () => {
        setOpenResetContinuousDialog(false);
        setResettingContinuous(true);
        try {
            const result = await BedrockIdeaService.resetAllContinuousOff();
            if (result.success) {
                handleSnackBar(
                    "success",
                    result.updated === 0
                        ? "No ideas had Continuous enabled."
                        : `Turned off Continuous for ${result.updated} idea(s).`
                );
            } else if (result.updated > 0) {
                const errDetail =
                    result.errors?.length > 0
                        ? result.errors.map((e) => e.error || e.id).filter(Boolean).join("; ")
                        : "Unknown error";
                handleSnackBar("warning", `Updated ${result.updated} idea(s). Errors: ${errDetail}`);
            } else {
                const errDetail =
                    result.errors?.length > 0
                        ? result.errors.map((e) => e.error || e.id).filter(Boolean).join("; ")
                        : "Reset failed";
                handleSnackBar("error", errDetail);
            }
            setReload(moment());
        } catch (e) {
            Logger.error("Reset all continuous:", e);
            handleSnackBar("error", "Failed to reset Continuous flags.");
            setReload(moment());
        } finally {
            setResettingContinuous(false);
        }
    };

    const handleRestoreStandaloneFlags = async () => {
        setRestoringStandalone(true);
        try {
            const result = await BedrockIdeaService.restoreStandaloneFlags();
            if (result.success) {
                handleSnackBar(
                    "success",
                    result.restored === 0
                        ? "No standalone ideas needed repair."
                        : `Restored standalone flag on ${result.restored} idea(s).`
                );
                setReload(moment());
            } else {
                handleSnackBar("error", result.error || "Failed to restore standalone flags");
            }
        } catch (e) {
            Logger.error("Restore standalone flags:", e);
            handleSnackBar("error", "Failed to restore standalone flags.");
        } finally {
            setRestoringStandalone(false);
        }
    };

    const handleTagClick = (tag) => {
        setSelectedTag((prev) => (prev === tag ? null : tag));
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleContinuousFilterChange = (_event, value) => {
        if (value != null) {
            setFilterContinuous(value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
    };

    const handleExhaustedFilterChange = (_event, value) => {
        if (value != null) {
            setFilterExhausted(value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
    };

    const handleTypeFilterClick = (filterKey) => {
        if (filterKey === "genHiddenWordsearch") {
            setFilterGenHiddenWordsearch((prev) => !prev);
        } else if (filterKey === "genWordWheel") {
            setFilterGenWordWheel((prev) => !prev);
        } else if (filterKey === "genStandaloneImage") {
            setFilterGenStandaloneImage((prev) => !prev);
        } else if (filterKey === "genWordSearch9") {
            setFilterGenWordSearch9((prev) => !prev);
        }
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleToggleEnabled = async (idea, newEnabledValue) => {
        try {
            const result = await BedrockIdeaService.updateIdea(idea.id, { enabled: newEnabledValue });
            if (result.success) {
                // Update local state optimistically
                setIdeas(prevIdeas =>
                    prevIdeas.map(item =>
                        item.id === idea.id ? { ...item, enabled: newEnabledValue } : item
                    )
                );
                handleSnackBar("success", `Idea "${idea.title}" ${newEnabledValue ? 'enabled' : 'disabled'} successfully`);
            } else {
                handleSnackBar("error", result.error || "Failed to update enabled status");
                // Refresh to get correct state
                setReload(moment());
            }
        } catch (error) {
            Logger.error("Error toggling enabled status:", error);
            handleSnackBar("error", "Failed to update enabled status. Please try again.");
            // Refresh to get correct state
            setReload(moment());
        }
    };

    const handleColumnOrderChange = useCallback((params) => {
        setColumnOrder((prev) => {
            const order =
                prev.length > 0
                    ? [...prev]
                    : getColumns({
                          onToggleEnabled: () => {},
                          onToggleRoundRobin: () => {},
                          onExecuteClick: () => {},
                      }).map((col) => col.field);
            const newOrder = [...order];
            const field = params.column?.field ?? newOrder[params.oldIndex];
            const fromIndex = newOrder.indexOf(field);
            if (fromIndex === -1) {
                newOrder.splice(params.oldIndex, 1);
                newOrder.splice(params.targetIndex, 0, field);
            } else {
                newOrder.splice(fromIndex, 1);
                newOrder.splice(params.targetIndex, 0, field);
            }
            return newOrder;
        });
    }, []);

    const baseColumns = getColumns({
        onToggleEnabled: handleToggleEnabled,
        onToggleRoundRobin: handleToggleRoundRobin,
        onExecuteClick: handleExecuteClick
    });
    const columns = applyColumnOrder(baseColumns, columnOrder).map((col) => {
        const w = columnWidths[col.field];
        if (w == null || w <= 0) {
            return col;
        }
        return { ...col, width: w, flex: undefined };
    });

    return (
        <Box sx={{ flexGrow: 1, p: 2, width: "100%", overflow: "auto" }}>
            <Grid container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
                <Grid size={12}>
                    <Typography variant="h5" gutterBottom>
                        Bedrock Idea Manager
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            flexWrap: "wrap",
                            gap: 1.5,
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            <FiberManualRecordIcon
                                sx={{
                                    fontSize: 10,
                                    color: roundRobinSchedulerOn === true ? "success.main" : "action.disabled"
                                }}
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    color: roundRobinSchedulerOn === true ? "success.main" : "text.secondary",
                                    fontWeight: 500
                                }}
                            >
                                {roundRobinSchedulerOn === true ? "On" : roundRobinSchedulerOn === false ? "Off" : "—"}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                minWidth: 0,
                                flex: { sm: 1 },
                                "& .MuiButton-root": {
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                    width: { xs: "100%", sm: "auto" },
                                },
                            }}
                        >
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<LoopIcon />}
                                onClick={handleOpenRoundRobin}
                            >
                                Continuous
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<ToggleOffIcon />}
                                disabled={resettingContinuous}
                                onClick={() => setOpenResetContinuousDialog(true)}
                            >
                                Reset continuous
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RestoreIcon />}
                                disabled={restoringStandalone}
                                onClick={handleRestoreStandaloneFlags}
                            >
                                Restore standalone
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreate}
                            >
                                Add Idea
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {!loadingTags && availableTags.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {availableTags.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            onClick={() => handleTagClick(tag)}
                            color={selectedTag === tag ? "primary" : "default"}
                            variant={selectedTag === tag ? "filled" : "outlined"}
                            size="small"
                        />
                    ))}
                </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="bedrock-idea-sort-label">Order by</InputLabel>
                    <Select
                        labelId="bedrock-idea-sort-label"
                        label="Order by"
                        value={sortOptionValue(sortModel)}
                        onChange={(event) => {
                            const [field, direction] = String(event.target.value).split(":");
                            setSortModel([{ field, sort: direction }]);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                        Continuous:
                    </Typography>
                    <ToggleButtonGroup
                        value={filterContinuous}
                        exclusive
                        onChange={handleContinuousFilterChange}
                        size="small"
                    >
                        <ToggleButton value="all" sx={{ minHeight: 44 }}>All</ToggleButton>
                        <ToggleButton value="only" sx={{ minHeight: 44 }}>Only</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                        Exhausted:
                    </Typography>
                    <ToggleButtonGroup
                        value={filterExhausted}
                        exclusive
                        onChange={handleExhaustedFilterChange}
                        size="small"
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="yes">Yes</ToggleButton>
                        <ToggleButton value="no">No</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                        Type:
                    </Typography>
                    <Chip
                        label="Hidden / Word Search"
                        onClick={() => handleTypeFilterClick("genHiddenWordsearch")}
                        color={filterGenHiddenWordsearch ? "primary" : "default"}
                        variant={filterGenHiddenWordsearch ? "filled" : "outlined"}
                        size="small"
                    />
                    <Chip
                        label="Word Wheel"
                        onClick={() => handleTypeFilterClick("genWordWheel")}
                        color={filterGenWordWheel ? "secondary" : "default"}
                        variant={filterGenWordWheel ? "filled" : "outlined"}
                        size="small"
                    />
                    <Chip
                        label="Standalone Image"
                        onClick={() => handleTypeFilterClick("genStandaloneImage")}
                        color={filterGenStandaloneImage ? "primary" : "default"}
                        variant={filterGenStandaloneImage ? "filled" : "outlined"}
                        size="small"
                    />
                    <Chip
                        label="Word Search Mini"
                        onClick={() => handleTypeFilterClick("genWordSearch9")}
                        color={filterGenWordSearch9 ? "primary" : "default"}
                        variant={filterGenWordSearch9 ? "filled" : "outlined"}
                        size="small"
                    />
                </Box>
            </Box>

            <Box sx={{ width: "100%", overflow: "auto" }}>
                <DataGrid
                    rows={ideas}
                    columns={columns}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(newModel) => {
                        setSortModel(newModel);
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    onColumnWidthChange={(params) => {
                        setColumnWidths((prev) => ({
                            ...prev,
                            [params.colDef.field]: params.width,
                        }));
                    }}
                    onColumnOrderChange={handleColumnOrderChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    disableRowSelectionOnClick
                    onRowDoubleClick={(params) => handleOpenEdit(params.row)}
                    autoHeight
                    sx={{ width: "100%", minWidth: 0, minHeight: 400, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
                Don&apos;t use the english or french, kids for tag. For Word Wheel ideas, enable &quot;Generate Word Wheel puzzles only&quot; — words are 3–8 letters and may share letters (e.g. LADY, LAY, DAY). For beautiful images (landscapes, animals, cakes…), enable &quot;Generate standalone images only&quot; — creates DRAFT sliding/jigsaw for admin review.
            </Alert>

            <BiContSchedulerDialog
                open={openRoundRobinDialog}
                onClose={() => setOpenRoundRobinDialog(false)}
                onSuccess={() => {
                    handleSnackBar("success", "Scheduler config updated");
                    fetchRoundRobinSchedulerStatus();
                }}
                onError={(msg) => handleSnackBar("error", msg)}
            />

            <ConfirmDialog
                open={openResetContinuousDialog}
                setOpen={setOpenResetContinuousDialog}
                title="Reset Continuous"
                message={[
                    "Turn off Continuous (round-robin) for every Bedrock idea in the catalog.",
                    "This only changes each idea’s Continuous switch — not type flags (standalone / word wheel) or scheduler dialog settings."
                ]}
                targetId={null}
                callback={handleConfirmResetAllContinuous}
            />

            {/* Execute Confirmation Dialog */}
            <ConfirmDialog
                open={openExecuteDialog}
                setOpen={setOpenExecuteDialog}
                title="Execute Bedrock Idea"
                message={[
                    "Would you like to run this idea now?",
                    "It takes few minutes to generate puzzles, take time."
                ]}
                targetName={ideaToExecute?.title}
                targetId={ideaToExecute?.id}
                callback={handleConfirmExecute}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
